/* @flow */
import * as React from 'react';
import { autorun, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';

import FinderWrapper from 'components/FinderWrapper/FinderWrapper';
import FinderGrid from 'components/FinderGrid/FinderGrid';

import Radio from '@material-ui/core/Radio';
import Switch from '@material-ui/core/Switch';
import FormGroup from '@material-ui/core/FormGroup';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import ArchiveLocationByCompany from 'views/ActionDrawer/forms/locations/ArchiveLocationByCompany/ArchiveLocationByCompany';

import locationColumns from './locationColumns';
import LocationColumn from './LocationColumn/LocationColumn';

import AddNewCompany from '../../views/ActionDrawer/forms/manageLocationActions/AddNewCompany/AddNewCompany';
import AddNewSite from '../../views/ActionDrawer/forms/manageLocationActions/AddNewSite/AddNewSite';
import AddNewBuilding from '../../views/ActionDrawer/forms/manageLocationActions/AddNewBuilding/AddNewBuilding';
import AddNewRoom from '../../views/ActionDrawer/forms/manageLocationActions/AddNewRoom/AddNewRoom';
import AddNewSystem from '../../views/ActionDrawer/forms/manageLocationActions/AddNewSystem/AddNewSystem';
import ActionDrawerWrapper from '../ActionDrawerWrapper/ActionDrawerWrapper';
import MarkAsDefaultLocation from '../../views/ActionDrawer/forms/locations/MarkOrUnmarkAsDefaultLocation/MarkAsDefaultLocation';
import UnmarkAsDefaultLocation from '../../views/ActionDrawer/forms/locations/MarkOrUnmarkAsDefaultLocation/UnmarkAsDefaultLocation';
import {
  ArchiveLocation,
  AssignUsers,
  RemoveUsers,
  StartAuditByLocations,
  UnarchiveLocation,
} from '../../views/ActionDrawer';
import PageSearch from '../PageSearch';

import styles from './LocationManager.module.scss';

const config: Object = {
  companyId: '',
  countryId: '',
  siteId: '',
  buildingId: '',
  roomId: '',
  workspace: '',
};

@translate()
@inject('accountStore', 'actionDrawerStore')
@observer
export default class LocationManager extends React.Component<*> {
  @observable searchTerm = '';

  @observable localState = {
    isLoading: false,
    searchPerformed: false,
  };

  componentDidMount() {
    const accountStore = this.props.accountStore;
    const accountId: string = this.props.id;
    const locationColumn = accountStore.locationColumn;

    if (accountStore.locationLists.length === 0 || accountStore.accountId !== accountId) {
      accountStore.resetLocations();
      accountStore.loadCompanies(accountId);
    } else if (locationColumn !== 0) {
      accountStore.updateLocations(
        accountStore.locationSelections[locationColumn],
        locationColumn,
        false
      );
    }

    autorun(() => {
      if (this.searchTerm) {
        this.searchPerformed = true;
        accountStore.loadCompanies(accountId, [this.searchTerm]);
      } else if (this.searchTerm === '' && this.searchPerformed) {
        this.searchPerformed = false;
        accountStore.loadCompanies(accountId, []);
      }
    });
  }

  getCurrentId = (data: Array, item: Object) => {
    return toJS(data)
      .filter((el: Object) => {
        return el.id === item.id;
      })
      .map((el: Object) => {
        return el.id;
      })
      .join();
  };

  handleUrls = (pathChooser: string, data: Object, item: Object) => {
    switch (pathChooser) {
      case 'company':
        config.companyId = this.getCurrentId(data, item);
        break;
      case 'country':
        config.countryId = this.getCurrentId(data, item);
        break;
      case 'site':
        config.siteId = this.getCurrentId(data, item);
        break;
      case 'building':
        config.buildingId = this.getCurrentId(data, item);
        break;
      case 'room':
        config.roomId = this.getCurrentId(data, item);
        break;
    }
  };

  updateLocation = (selected: *, idx: number) => {
    const accountStore: Object = this.props.accountStore;

    // If one item of list already clicked then,
    // do nothing while loadingLocations is running
    if (!accountStore.loadingLocations) {
      accountStore.updateLocations(selected, idx);
      accountStore.locationColumn = idx;
    }
  };

  async onTypeChange(event, value: string) {
    const accountStore: Object = this.props.accountStore;
    accountStore.searchType = value;
    accountStore.resetLocations();
    this.localState.isLoading = true;
    await accountStore.loadCompanies(accountStore.accountId);
    this.localState.isLoading = false;
  }

  async onCaasFilter(event, value) {
    const accountStore: Object = this.props.accountStore;
    accountStore.isCaasActive = value;
    accountStore.resetLocations();
    this.localState.isLoading = true;
    await accountStore.loadCompanies(accountStore.accountId);
    this.localState.isLoading = false;
  }

  /** Renders a `Switch` if the account has Asset request or Rush Asset request enabled.
   * If it has both enabled, it will render a `Radio Group`. But if none are enabled, it
   * will go to `null` since the filter toggle is not needed.
   */
  renderLocationTypeSelector() {
    const { accountStore } = this.props;

    const isAssetRequestEnabled: Boolean = accountStore.details.details.asset_request_form_enabled;
    const isRushAssetRequestEnabled: Boolean =
      accountStore.details.details.is_rush_asset_request_form_customer;

    // Rental Option should always be available.
    const options = [{ value: 'rental', label: 'Rental Locations' }];

    // Add asset request option if it's enabled in the account.
    if (isAssetRequestEnabled) {
      options.push({ value: 'asset-request', label: 'Asset Request Locations' });
    }

    // Add rush asset request option if it's enabled in the account.
    if (isRushAssetRequestEnabled) {
      options.push({ value: 'rush-asset-request', label: 'Rush Asset Request Locations' });
    }

    // If either Rush Asset Request or Asset Request is enabled, it should show a Switch.
    if (options.length === 2) {
      return (
        <div className={`${styles['loc-mngmnt']} ${styles['loc-mngmnt__switch-toggle']}`}>
          <b>{options[0].label}</b>
          <Switch
            checked={accountStore.searchType === options[1].value}
            disabled={this.localState.isLoading}
            color="primary"
            onChange={(e, checked) => {
              const newValue = checked ? options[1].value : options[0].value;
              this.onTypeChange(e, newValue);
            }}
          />
          <b>{options[1].label}</b>
        </div>
      );
    }

    // If both are enabled it will show a Radio Group.
    if (options.length > 2) {
      return (
        <FormControl component="fieldset" disabled={this.localState.isLoading}>
          <FormGroup row className={`${styles['loc-mngmnt']} ${styles['loc-mngmnt__radio-group']}`}>
            {options.map(opt => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Radio
                    checked={accountStore.searchType === opt.value}
                    onChange={e => this.onTypeChange(e, opt.value)}
                    value={opt.value}
                    color="primary"
                    className={styles['loc-mngmnt__radio-control']}
                  />
                }
                label={<span className={styles['loc-mngmnt__radio-label']}>{opt.label}</span>}
              />
            ))}
          </FormGroup>
        </FormControl>
      );
    }
    // If none are enabled, only Rental is present and there's no need for toggles.
    return null;
  }

  render() {
    const { t, actionDrawerStore, accountStore } = this.props;
    const { details } = accountStore;
    const isCaaSCustomer: Boolean = accountStore.details.details.is_caas_customer;
    return (
      <React.Fragment>
        <PageSearch
          viewConfig={{
            codename: 'account-locations',
            title: '',
            type: 'SINGLE',
            userFilterNamespace: '',
            view: { columns: [], dataPathname: '' },
            disableFilters: true,
          }}
          onSearchSubmit={newSearchTerm => {
            this.searchTerm = newSearchTerm;
          }}
          onClearSearch={() => {
            this.searchTerm = '';
          }}
          searchTerm={this.searchTerm}
        />
        <FinderWrapper>
          {this.renderLocationTypeSelector()}
          {isCaaSCustomer && (
            <div className={`${styles['loc-mngmnt__switch-toggle']}`}>
              <Switch
                color="primary"
                checked={accountStore.isCaasActive}
                disabled={this.localState.isLoading}
                onChange={this.onCaasFilter.bind(this)}
              />
              <b>CaaS Locations Only</b>
            </div>
          )}
          <FinderGrid>
            {locationColumns.map((item: Object, index: number) => {
              const locationColumnList =
                index < accountStore.getLocationLists.length
                  ? accountStore.getLocationLists[index]
                  : [];

              const selectedItem: Object =
                index < accountStore.locationSelections.length
                  ? accountStore.locationSelections[index]
                  : null;

              return (
                <LocationColumn
                  locationUrlConfig={{
                    updatedId: this.handleUrls,
                  }}
                  config={config}
                  key={index.toString()}
                  index={index}
                  finderColumn={{
                    columnTitle: t(item.title),
                    columnData: locationColumnList,
                    displayCount: true,
                    searchList: true,
                  }}
                  pathTo={item.pathTo}
                  expandable={item.expandable}
                  blankText={t(item.blankText)}
                  linkText={t(item.linkText)}
                  action={item.action}
                  placeholder={t(item.placeholder)}
                  selectedItem={selectedItem}
                  pathChooser={item.pathChooser}
                  onSelect={this.updateLocation}
                />
              );
            })}
          </FinderGrid>
          <ActionDrawerWrapper
            appendActionToURL={true}
            onActionCompleted={() => {
              /*overriding with empty fn*/
            }}
          >
            {
              {
                addNewCompany: <AddNewCompany params={this.props.params} />,
                addNewSite: <AddNewSite params={this.props.params} config={config} />,
                addNewBuilding: <AddNewBuilding params={this.props.params} config={config} />,
                addNewRoom: <AddNewRoom params={this.props.params} config={config} />,
                addNewSystem: <AddNewSystem params={this.props.params} config={config} />,
                archiveLocationsByCompany: (
                  <ArchiveLocationByCompany location={actionDrawerStore.currentActionDrawerData} />
                ),
                markAsDefaultLocation: (
                  <MarkAsDefaultLocation location={actionDrawerStore.currentActionDrawerData} />
                ),
                unmarkAsDefaultLocation: (
                  <UnmarkAsDefaultLocation location={actionDrawerStore.currentActionDrawerData} />
                ),
                archiveLocation: (
                  <ArchiveLocation location={actionDrawerStore.currentActionDrawerData} />
                ),
                unarchiveLocation: (
                  <UnarchiveLocation location={actionDrawerStore.currentActionDrawerData} />
                ),
                startAuditByLocations: (
                  <StartAuditByLocations
                    location={actionDrawerStore.currentActionDrawerData}
                    type="locations"
                  />
                ),
                assignUsers: (
                  <AssignUsers
                    accountId={details.details.id}
                    location={actionDrawerStore.currentActionDrawerData}
                  />
                ),
                removeUsers: (
                  <RemoveUsers
                    accountId={details.details.id}
                    location={actionDrawerStore.currentActionDrawerData}
                  />
                ),
              }[actionDrawerStore.currentActionDrawer]
            }
          </ActionDrawerWrapper>
        </FinderWrapper>
      </React.Fragment>
    );
  }
}
